/**
 * Shared Task Handler Framework
 * Provides common functionality for handling tasks across different workers
 */

import { Logger } from '@/shared/logger/logger';

import { TaskResult } from './worker_utils';

/**
 * Base interface for task handler parameters
 */
export type TaskHandlerParams = Record<string, any>;

/**
 * Abstract task handler function type
 */
export type TaskHandlerFunction = (payload: any) => Promise<TaskResult>;

/**
 * Registry of task handlers
 */
export type TaskHandlerRegistry = Record<string, TaskHandlerFunction>;

/**
 * Common test task handler
 * Can be reused across different workers for testing
 */
export async function commonTestHandler(payload: {
  message?: string;
}): Promise<TaskResult> {
  try {
    return {
      data: {
        echo: payload?.message || 'Test successful',
        success: true,
      },
      success: true,
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : String(error),
      success: false,
    };
  }
}

/**
 * Utility function for API calls with retry logic
 * Common functionality for making API calls with retries and exponential backoff
 */
export async function callAPI(
  url: string,
  body: any,
  retries = 3,
  workerName = 'Worker',
): Promise<TaskResult> {
  let lastError;
  const BASE_RETRY_DELAY_MS = 500;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch(url, {
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
        keepalive: true,
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const responseData = await response.json();

      return {
        data: responseData,
        success: true,
      };
    } catch (error: any) {
      lastError = error;
      Logger.error(
        `[${workerName}] API call attempt ${attempt + 1} failed:`,
        error.message,
      );

      // Wait before retrying (exponential backoff)
      if (attempt < retries - 1) {
        await new Promise(resolve =>
          setTimeout(resolve, Math.pow(2, attempt) * BASE_RETRY_DELAY_MS),
        );
      }
    }
  }

  return {
    error: lastError?.message || 'API call failed after retries',
    success: false,
  };
}

/**
 * Base task processor class that can be extended by specific workers
 */
export abstract class BaseTaskProcessor {
  /**
   * Registry of task handlers for this processor
   */
  public taskHandlers: TaskHandlerRegistry = {};

  /**
   * Worker name for logging purposes
   */
  protected abstract workerName: string;

  /**
   * Constructor
   * @param additionalHandlers - Additional handlers to register
   */
  constructor(additionalHandlers: TaskHandlerRegistry = {}) {
    // Register common handlers
    this.registerCommonHandlers();

    // Register additional handlers
    this.registerTaskHandlers(additionalHandlers);
  }

  /**
   * Register common handlers that should be available in all workers
   */
  private registerCommonHandlers(): void {
    this.taskHandlers['TEST'] = commonTestHandler;
  }

  /**
   * Register task handlers
   * @param handlers - Handlers to register
   */
  protected registerTaskHandlers(handlers: TaskHandlerRegistry): void {
    this.taskHandlers = {
      ...this.taskHandlers,
      ...handlers,
    };
  }

  /**
   * Process a task
   * @param type - Task type
   * @param payload - Task payload
   */
  public async processTask(type: string, payload: any): Promise<TaskResult> {
    const handler = this.taskHandlers[type];

    if (!handler) {
      Logger.error(
        `[${this.workerName}] No handler found for task type: ${type}`,
      );
      return {
        error: `No handler found for task type: ${type}`,
        success: false,
      };
    }

    try {
      Logger.info(`[${this.workerName}] Processing task: ${type}`);
      return await handler(payload);
    } catch (error: any) {
      Logger.error(
        `[${this.workerName}] Error processing task ${type}:`,
        error,
      );
      return {
        error: error instanceof Error ? error.message : String(error),
        success: false,
      };
    }
  }
}
